/* globals navigator */

import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { call, put, takeLatest, all } from 'redux-saga/effects';

import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';
import { getClubMembership } from 'redux/clubMemberships/api';
import { GET_CLUB_MEMBERSHIP_SUCCESS } from 'redux/clubMemberships/constants';
import { get as getClub } from 'redux/clubs/sagas';
import { add as addCustomer } from 'redux/customer/sagas';

import {
  getClubSignup,
  updateClubSignup,
  createClubSignup,
  processClubSignup,
  sendClubSignupToHostedPayment
} from './api';
import {
  UPDATE_CLUB_SIGNUP_REQUEST,
  GET_CLUB_SIGNUP_REQUEST,
  GET_CLUB_SIGNUP_HOSTED_PAYMENT_REQUEST,
  GET_CLUB_SIGNUP_REQUESTING,
  GET_CLUB_SIGNUP_SUCCESS,
  RESET_CLUB_SIGNUP_ISFETCHING,
  UPDATE_CLUB_SIGNUP_WITH_PASSWORD_REQUEST,
  UPDATE_CLUB_SIGNUP_WITH_PASSWORD_AND_PROCESS_REQUEST,
  CREATE_CLUB_SIGNUP_REQUEST,
  UPDATE_AND_PROCESS_CLUB_SIGN_UP_REQUEST,
  CLUB_SIGNUP_STEP_REQUEST,
  CLUB_SIGNUP_STEP_REQUEST_GTM,
  CHARGE_CLUB_SIGNUP_SUCCESS_GTM,
  SEND_CLUB_SIGNUP_TO_HOSTED_PAYMENT_REQUEST
} from './constants';

export function* get() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    if (!clubSignupId()) {
      yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: {} });
    } else {
      const response = yield call(getClubSignup, clubSignupId());
      yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: response.data });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('clubSignupId');
      yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: {} });
    } else {
      yield put({ type: RESET_CLUB_SIGNUP_ISFETCHING });
      yield call(addAPIError, error);
    }
  }
}

function* getForHostedPayment(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    const response = yield call(getClubSignup, clubSignupId());
    yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: response.data });
    const clubSignup = response.data;
    if (clubSignup.status === 'Completed') {
      Cookies.remove('clubSignupId');
      const membershipResponse = yield call(
        getClubMembership,
        clubSignup.customerId,
        clubSignup.clubMembershipId
      );
      yield put({
        type: GET_CLUB_MEMBERSHIP_SUCCESS,
        payload: membershipResponse.data
      });
      if (action.successFunction) {
        // Send the cart rather than the order
        action.successFunction(membershipResponse.data);
      }
    } else {
      action.errorFunction(clubSignup);
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('clubSignupId');
      yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: {} });
    } else {
      yield put({ type: RESET_CLUB_SIGNUP_ISFETCHING });
      yield call(addAPIError, error);
    }
  }
}

export function* create(action) {
  try {
    const club = yield call(getClub, { slug: action.clubSlug });
    if (!club) {
      // We need to throw an error if no club
      return null;
    }
    const newClubSignup = {
      clubId: club.id,
      connectionInformation: {
        userAgent: navigator.userAgent
      }
    };
    if (club.type === 'Subscription') {
      newClubSignup.subscription = {
        frequency: club.subscription.defaultFrequency
      };
    }
    if (!clubSignupId()) {
      if (Cookies.get('customerToken')) {
        newClubSignup.customerId = jwtDecode(Cookies.get('customerToken')).sub;
      }
      yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
      newClubSignup.acquisitionChannel = 'Web';
      const response = yield call(createClubSignup, newClubSignup);
      Cookies.set('clubSignupId', response.data.id, {
        expires: 365,
        secure: process.env.NODE_ENV === 'production'
      });
      yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: response.data });
    } else {
      yield call(update, { clubSignup: newClubSignup });
    }
    return null;
  } catch (error) {
    yield call(addAPIError, error);
    return null;
  }
}

export function* update(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    const response = yield call(
      updateClubSignup,
      clubSignupId(),
      action.clubSignup
    );
    yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: response.data });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* updateAndProcess(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    const clubSignup = yield call(
      updateClubSignup,
      clubSignupId(),
      action.clubSignup
    );
    yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: clubSignup.data });
    const finalClubSignup = yield call(processClubSignup, clubSignupId());
    yield put({ type: GET_CLUB_SIGNUP_SUCCESS, payload: finalClubSignup.data });
    Cookies.remove('clubSignupId');
    const response = yield call(
      getClubMembership,
      finalClubSignup.data.customerId,
      finalClubSignup.data.clubMembershipId
    );
    yield put({ type: GET_CLUB_MEMBERSHIP_SUCCESS, payload: response.data });
    yield put({
      type: CHARGE_CLUB_SIGNUP_SUCCESS_GTM,
      payload: response.data
    });
    if (action.successFunction) {
      action.successFunction(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
  }
}

function* sendToHostedPayment(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    const response = yield call(sendClubSignupToHostedPayment, clubSignupId());
    action.redirectFunction(response.data);
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* updatePassword(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    const { clubSignup } = action;
    const customer = {
      firstName: clubSignup.billTo.firstName,
      lastName: clubSignup.billTo.lastName,
      city: clubSignup.billTo.city,
      stateCode: clubSignup.billTo.stateCode,
      countryCode: clubSignup.billTo.countryCode,
      emails: [{ email: clubSignup.email }],
      phones: [{ phone: clubSignup.billTo.phone }],
      password: action.data.password
    };
    // Login (which should also try and add itself to clubSignup)
    yield call(addCustomer, { customer });
    if (action.successFunction) {
      action.successFunction();
    }
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* updatePasswordAndProcess(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CLUB_SIGNUP_REQUESTING });
    const { originalClubSignup } = action;
    const customer = {
      firstName: originalClubSignup.billTo.firstName,
      lastName: originalClubSignup.billTo.lastName,
      city: originalClubSignup.billTo.city,
      stateCode: originalClubSignup.billTo.stateCode,
      countryCode: originalClubSignup.billTo.countryCode,
      emails: [{ email: originalClubSignup.email }],
      phones: [{ phone: originalClubSignup.billTo.phone }],
      password: action.data.password
    };
    // Login (which should also try and add itself to clubSignup)
    yield call(addCustomer, { customer });
    const clubSignup = {
      clubId: action.data.clubId
    };
    // At this point we should be able to process??
    yield call(updateAndProcess, {
      clubSignup,
      successFunction: action.successFunction
    });
  } catch (error) {
    yield call(addAPIError, error);
  }
}

export function* checkoutStep(action) {
  const { step } = action.payload;
  yield put({
    type: CLUB_SIGNUP_STEP_REQUEST_GTM,
    payload: {
      step
    }
  });
}

export function* watchList() {
  yield takeLatest(GET_CLUB_SIGNUP_REQUEST, get);
  yield takeLatest(GET_CLUB_SIGNUP_HOSTED_PAYMENT_REQUEST, getForHostedPayment);
  yield takeLatest(UPDATE_CLUB_SIGNUP_REQUEST, update);
  yield takeLatest(
    UPDATE_CLUB_SIGNUP_WITH_PASSWORD_AND_PROCESS_REQUEST,
    updatePasswordAndProcess
  );
  yield takeLatest(UPDATE_CLUB_SIGNUP_WITH_PASSWORD_REQUEST, updatePassword);
  yield takeLatest(UPDATE_AND_PROCESS_CLUB_SIGN_UP_REQUEST, updateAndProcess);
  yield takeLatest(CREATE_CLUB_SIGNUP_REQUEST, create);
  yield takeLatest(CLUB_SIGNUP_STEP_REQUEST, checkoutStep);
  yield takeLatest(
    SEND_CLUB_SIGNUP_TO_HOSTED_PAYMENT_REQUEST,
    sendToHostedPayment
  );
}

export default function* clubSignupSagas() {
  yield all([watchList()]);
}

const clubSignupId = () => Cookies.get('clubSignupId');
